import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import ShowChartRounded from "@mui/icons-material/ShowChartRounded";
import LogoutIcon from "@mui/icons-material/Logout";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import React, { useState } from "react";
import clsx from "clsx";
import { Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Popover, styled } from "@mui/material";
import { LocaleSelect } from "@packsize/localization";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { type HydratedUser } from "@packsize/identity-api-sdk";

import { NavigationMenuItem } from "../components/navigation-menu-item";

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
	minWidth: "fit-content",
	marginRight: theme.spacing(2),
	color: theme.palette.text.primary,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
	"&:hover": {
		color: theme.palette.text.primary,
	},
})) as typeof ListItemButton;

export interface UserMenuTemplateProps {
	readonly currentUser: HydratedUser | null | undefined;
	readonly showCompanyManagement: boolean;
	readonly onCompanyManagementClick: () => void;
}

export const UserMenuTemplate: React.FC<UserMenuTemplateProps> = ({
	currentUser,
	showCompanyManagement,
	onCompanyManagementClick,
}) => {
	const intl = useIntl();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const menuOpen = anchorEl !== null;

	return (
		<>
			<NavigationMenuItem
				dark
				className={clsx({ active: menuOpen === true })}
				data-cy="user-button"
				data-testid="user-menu-button"
				onClick={handleOpenMenu}
			>
				<AccountCircleOutlined />
			</NavigationMenuItem>

			<Popover
				open={menuOpen}
				anchorEl={anchorEl}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				sx={{ mt: 1 }}
			>
				<List data-testid="user-menu">
					<ListItem>
						<StyledListItemIcon>
							<AccountCircleOutlined />
						</StyledListItemIcon>

						<ListItemText primary={currentUser?.userName} />
					</ListItem>

					<Divider sx={{ my: 1 }} />

					<ListItem sx={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}>
						<LocaleSelect mode="postAuth" />
					</ListItem>

					<Divider sx={{ my: 1 }} />

					{showCompanyManagement === true && (
						<ListItem disablePadding>
							<StyledListItemButton
								onClick={() => {
									handleCloseMenu();
									onCompanyManagementClick();
								}}
								data-cy="company-management-button"
								data-testid="company-management-button"
								data-pendoid="user-menu__link__company-management"
							>
								<StyledListItemIcon>
									<CorporateFareOutlinedIcon />
								</StyledListItemIcon>

								<ListItemText
									primary={intl.formatMessage({ id: "Company Management", defaultMessage: "Company Management" })}
								/>
							</StyledListItemButton>
						</ListItem>
					)}
					{showCompanyManagement === true && <Divider sx={{ my: 1 }} />}

					<ListItem disablePadding>
						<StyledListItemButton
							component="a"
							href="https://docs.packnet.app/en/packnet-quarterly-release-highlights.html#whats-new"
							target="_blank"
							rel="noopener noreferrer"
							onClick={handleCloseMenu}
							data-pendoid="user-menu__link__release-highlights"
						>
							<StyledListItemIcon>
								<CampaignOutlinedIcon />
							</StyledListItemIcon>

							<ListItemText
								primary={intl.formatMessage({ id: "Release Highlights", defaultMessage: "Release Highlights" })}
							/>
						</StyledListItemButton>
					</ListItem>

					<ListItem disablePadding>
						<StyledListItemButton
							component="a"
							href="https://docs.packnet.app/"
							target="_blank"
							rel="noopener noreferrer"
							onClick={handleCloseMenu}
							data-pendoid="user-menu__link__documentation"
						>
							<StyledListItemIcon>
								<ArticleOutlinedIcon />
							</StyledListItemIcon>

							<ListItemText primary={intl.formatMessage({ id: "Documentation", defaultMessage: "Documentation" })} />
						</StyledListItemButton>
					</ListItem>

					<ListItem disablePadding>
						<StyledListItemButton
							component={Link}
							to="/status"
							onClick={handleCloseMenu}
							data-cy="status-button"
							data-pendoid="user-menu__link__status"
						>
							<StyledListItemIcon>
								<ShowChartRounded />
							</StyledListItemIcon>

							<ListItemText primary={intl.formatMessage({ id: "Status", defaultMessage: "Status" })} />
						</StyledListItemButton>
					</ListItem>

					<ListItem disablePadding>
						<StyledListItemButton
							component={Link}
							to="/support"
							onClick={handleCloseMenu}
							data-cy="support-button"
							data-pendoid="user-menu__link__support"
						>
							<StyledListItemIcon>
								<HeadsetMicOutlinedIcon />
							</StyledListItemIcon>

							<ListItemText primary={intl.formatMessage({ id: "Support", defaultMessage: "Support" })} />
						</StyledListItemButton>
					</ListItem>

					<ListItem disablePadding>
						<StyledListItemButton
							component={Link}
							to="/logged-out"
							onClick={handleCloseMenu}
							data-cy="logout-button"
							data-testid="logout-button"
							data-pendoid="user-menu__link__logout"
							sx={{ color: "error.main", "&:hover": { color: "error.main" } }}
						>
							<StyledListItemIcon>
								<LogoutIcon color="error" />
							</StyledListItemIcon>

							<ListItemText primary={intl.formatMessage({ id: "Logout", defaultMessage: "Logout" })} />
						</StyledListItemButton>
					</ListItem>
				</List>
			</Popover>
		</>
	);
};
